import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, H3, Paragraph, Separator, Spinner, XStack, YStack } from 'tamagui';
import { useChatViewState } from '../lib/chat-view-state';
import { MessageList } from '../components/chat/MessageList';
import { ChatComposer } from '../components/chat/ChatComposer';
import { ChatLiveRegion } from '../components/chat/ChatLiveRegion';
import { QuotaMeter } from '../components/chat/QuotaMeter';
import { ChatEmptyState } from '../components/chat/ChatEmptyState';

const DISCLAIMER =
  'AI guidance, not a substitute for a mechanic — for brakes, steering, smoke, or overheating, see a professional.';
const CONTENT_MAX_WIDTH = 860;
const TAP_TARGET_MIN = 44;

// react-router renders an <a>; strip the UA link styling and give it a >=44px
// hit area (flex box, not an inline text link). Reset + tap-target sizing only.
const BACK_LINK_STYLE: React.CSSProperties = {
  textDecoration: 'none',
  color: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: TAP_TARGET_MIN,
};

export function VehicleChat() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const chat = useChatViewState(id);

  if (chat.notFound) return <ChatNotFound onBack={() => navigate('/vehicles')} />;
  if (chat.loading) return <ChatLoading />;

  return (
    <YStack flex={1} width="100%" maxWidth={CONTENT_MAX_WIDTH} alignSelf="center" gap="$3">
      <YStack gap="$2">
        <XStack justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap="$2">
          <YStack gap="$1">
            <H3>{chat.vehicleLabel}</H3>
            <Link to={`/vehicles/${id}`} className="focus-ring" style={BACK_LINK_STYLE}>
              <Paragraph size="$2" color="$accent">
                ← Back to vehicle
              </Paragraph>
            </Link>
          </YStack>
          <QuotaMeter quota={chat.quota} />
        </XStack>
        <Paragraph size="$1" color="$color10">
          {DISCLAIMER}
        </Paragraph>
        <Separator />
      </YStack>

      {chat.messages.length === 0 ? (
        <ChatEmptyState onPick={chat.fillComposer} />
      ) : (
        <MessageList messages={chat.messages} />
      )}

      {/* Empty fallback live region, AFTER the messages so an assistant bubble
          (which carries its own aria-live) is the first polite region once the
          thread is non-empty; this only covers the empty-thread case. */}
      <ChatLiveRegion />

      <ChatComposer
        value={chat.input}
        onChange={chat.setInput}
        onSend={chat.handleSend}
        disabled={chat.composerDisabled}
        canSend={chat.canSend}
        exhausted={chat.exhausted}
        errorMessage={chat.errorMessage}
      />
    </YStack>
  );
}

function ChatLoading() {
  return (
    <YStack flex={1} alignItems="center" justifyContent="center" padding="$6">
      <Spinner />
    </YStack>
  );
}

function ChatNotFound({ onBack }: { onBack: () => void }) {
  return (
    <YStack gap="$3" padding="$4" alignItems="flex-start">
      <H3>Vehicle not found</H3>
      <Paragraph color="$color10">
        We couldn't find that vehicle, or you don't have access to it.
      </Paragraph>
      <Button theme="blue" minHeight={TAP_TARGET_MIN} onPress={onBack}>
        Back to vehicles
      </Button>
    </YStack>
  );
}
